import * as polygonClippingModule from 'polygon-clipping';

import type { Rect, Vec2 } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';
import type { MultiPolygon as PcMultiPolygon, Polygon as PcPolygon, Ring as PcRing } from 'polygon-clipping';

// the ESM build only provides a default export while the typings declare named exports
const polygonClipping =
    (polygonClippingModule as unknown as { default?: typeof polygonClippingModule }).default ?? polygonClippingModule;

/** Polygon with an outer ring followed by optional hole rings (rings are open, i.e. first != last). */
export type Polygon2D = Vec2[][];

const GEOMETRY_TOLERANCE = 1e-4;

export function signedArea(ring: Vec2[]): number {
    let area = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        area += (ring[j].x - ring[i].x) * (ring[j].y + ring[i].y);
    }
    return area / 2;
}

export function convexHull(points: Vec2[]): Vec2[] {
    const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    if (sorted.length < 3) {
        return sorted;
    }

    const cross = (o: Vec2, a: Vec2, b: Vec2) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower: Vec2[] = [];
    for (const p of sorted) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper: Vec2[] = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
        const p = sorted[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/** Removes duplicate and collinear vertices. */
export function simplifyRing(ring: Vec2[], tolerance = GEOMETRY_TOLERANCE): Vec2[] {
    const deduped: Vec2[] = [];
    for (const p of ring) {
        const last = deduped[deduped.length - 1];
        if (!last || Math.abs(last.x - p.x) > tolerance || Math.abs(last.y - p.y) > tolerance) {
            deduped.push(p);
        }
    }
    if (deduped.length > 1) {
        const first = deduped[0];
        const last = deduped[deduped.length - 1];
        if (Math.abs(last.x - first.x) <= tolerance && Math.abs(last.y - first.y) <= tolerance) {
            deduped.pop();
        }
    }

    let changed = true;
    let result = deduped;
    while (changed && result.length > 3) {
        changed = false;
        const next: Vec2[] = [];
        for (let i = 0; i < result.length; i++) {
            const prev = result[(i - 1 + result.length) % result.length];
            const curr = result[i];
            const following = result[(i + 1) % result.length];
            const cross = (curr.x - prev.x) * (following.y - prev.y) - (curr.y - prev.y) * (following.x - prev.x);
            const length = Math.hypot(following.x - prev.x, following.y - prev.y);
            if (length > 0 && Math.abs(cross) / length <= tolerance) {
                changed = true;
                continue;
            }
            next.push(curr);
        }
        result = next;
    }
    return result;
}

const toPcRing = (ring: Vec2[]): PcRing => ring.map((p) => [p.x, p.y] as [number, number]);
const fromPcRing = (ring: PcRing): Vec2[] => ring.slice(0, -1).map(([x, y]) => ({ x, y }));

/** Boolean union of polygons (outer rings only). Falls back to the convex hull if clipping fails. */
export function unionPolygons(rings: Vec2[][]): Polygon2D[] {
    const valid = rings.filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > GEOMETRY_TOLERANCE ** 2);
    if (valid.length === 0) {
        return [];
    }

    try {
        const geoms: PcPolygon[] = valid.map((r) => [toPcRing(r)]);
        const [first, ...rest] = geoms;
        const result: PcMultiPolygon = polygonClipping.union(first, ...rest);
        return result
            .map((polygon) => polygon.map((ring) => simplifyRing(fromPcRing(ring))).filter((ring) => ring.length >= 3))
            .filter((polygon) => polygon.length > 0);
    } catch {
        return [[convexHull(valid.flat())]];
    }
}

export function pointInRing(p: Vec2, ring: Vec2[]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i];
        const b = ring[j];
        if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}

function segmentsIntersect(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
    const orient = (p: Vec2, q: Vec2, r: Vec2) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
    const d1 = orient(c, d, a);
    const d2 = orient(c, d, b);
    const d3 = orient(a, b, c);
    const d4 = orient(a, b, d);
    return d1 * d2 < 0 && d3 * d4 < 0;
}

export function rectsOverlap(a: Rect, b: Rect, margin = 0): boolean {
    return (
        a.minX < b.maxX + margin && a.maxX > b.minX - margin && a.minY < b.maxY + margin && a.maxY > b.minY - margin
    );
}

export function rectContains(outer: Rect, inner: Rect): boolean {
    return (
        inner.minX >= outer.minX && inner.maxX <= outer.maxX && inner.minY >= outer.minY && inner.maxY <= outer.maxY
    );
}

/** Exact intersection test between an axis aligned rectangle and a simple ring. */
export function rectIntersectsRing(rect: Rect, ring: Vec2[]): boolean {
    for (const p of ring) {
        if (p.x > rect.minX && p.x < rect.maxX && p.y > rect.minY && p.y < rect.maxY) {
            return true;
        }
    }

    const corners: Vec2[] = [
        { x: rect.minX, y: rect.minY },
        { x: rect.maxX, y: rect.minY },
        { x: rect.maxX, y: rect.maxY },
        { x: rect.minX, y: rect.maxY }
    ];
    if (corners.some((c) => pointInRing(c, ring))) {
        return true;
    }

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        for (let k = 0; k < 4; k++) {
            if (segmentsIntersect(ring[j], ring[i], corners[k], corners[(k + 1) % 4])) {
                return true;
            }
        }
    }
    return false;
}
